import { useId, useState } from "react";

import { useChatSaveInfosec } from "~/ai-chat/feature/use-chat-save-infosec";
import { useChatSaveTarget } from "~/ai-chat/feature/use-chat-save-target";
import { useSaveChatResource } from "~/ai-chat/feature/use-save-chat-resource";
import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { ChatSaveTranscriptDialog } from "~/ai-chat/ui/chat-save-transcript-dialog";
import { ControlTagMultiSelect } from "~/control-tags/feature/control-tag-multi-select";

interface ChatSaveTranscriptProps {
  messages: ChatTranscriptItem[];
  defaultName: string;
  onClose: () => void;
}

// The save-chat flow (no sharing — the Share button handles that), mounted only while its dialog is open
// so resolving the user's allowed infosec levels (which fetches the org user list) never runs on every
// chat-widget mount.
export function ChatSaveTranscript({ messages, defaultName, onClose }: ChatSaveTranscriptProps) {
  const controlTagsId = useId();
  const [controlTagIds, setControlTagIds] = useState<string[]>([]);
  const target = useChatSaveTarget();
  const destination = target.status === "ready" ? target : undefined;
  const { save, isSaving } = useSaveChatResource(destination);
  const { infosecEnabled, infosecLevels, defaultInfosecLevel } = useChatSaveInfosec();

  return (
    <ChatSaveTranscriptDialog
      open
      defaultName={defaultName}
      infosecEnabled={infosecEnabled}
      infosecLevels={infosecLevels}
      defaultInfosecLevel={defaultInfosecLevel}
      destination={
        target.status === "loading"
          ? undefined
          : destination
            ? { systemName: destination.systemName, branchName: destination.branch.name }
            : null
      }
      controlTagsField={
        <ControlTagMultiSelect
          id={controlTagsId}
          label={<span className="text-xs">Control tags</span>}
          value={controlTagIds}
          onChange={setControlTagIds}
          disabled={isSaving}
        />
      }
      isSaving={isSaving}
      onSave={(name, choices) => {
        void save(name, messages, { ...choices, controlTagIds }).then((resourceId) => {
          if (resourceId) onClose();
        });
      }}
      onCancel={onClose}
    />
  );
}
